import React, { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowUpRight, ChevronDown } from 'lucide-react';
import { useReducedMotion } from 'framer-motion';

const Orb = lazy(() => import('./vendor/ElevenLabsOrb').then(module => ({ default: module.Orb })));

class OrbFallback extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() { return this.state.failed ? null : this.props.children; }
}

/** Decorative only: never requests microphone, audio or an AI connection. */
export function LatoOrb({ animated = false, busy = false }: { animated?: boolean; busy?: boolean }) {
  const initialReduced = useReducedMotion();
  const [motionPreference, setMotionPreference] = useState<boolean | null>(null);
  const reduced = motionPreference ?? initialReduced;
  const [visible, setVisible] = useState(document.visibilityState !== 'hidden');
  useEffect(() => {
    if (!window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = (event: MediaQueryListEvent) => setMotionPreference(event.matches);
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  useEffect(() => {
    const update = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  return <span className={'lato-ai-orb' + (busy ? ' is-busy' : '')} aria-hidden="true">
    <span className="lato-ai-orb-fallback" />
    {animated && !reduced && visible && <OrbFallback><Suspense fallback={null}>
      <Orb className="lato-ai-orb-webgl" colors={['#67d5d8', '#6785df']} seed={24} agentState={busy ? 'thinking' : null} />
    </Suspense></OrbFallback>}
  </span>;
}

export function LatoAiLauncher({ open, busy, onClick, disabled, editOnly = false }: {
  open: boolean; busy?: boolean; onClick: () => void; disabled?: boolean; editOnly?: boolean;
}) {
  const button = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open && !button.current?.closest('[inert]')) button.current?.focus({ preventScroll: true });
    wasOpen.current = open;
  }, [open]);
  return <button ref={button} type="button" className={'lato-ai-launcher' + (open ? ' is-open' : '')}
    aria-label={open ? 'Close Lato AI' : 'Ask Lato AI'} aria-expanded={open}
    title={editOnly ? 'Lato AI · Edit this document' : 'Ask Lato AI'} disabled={disabled} onClick={onClick}>
    <LatoOrb busy={busy} /><span>{busy ? 'Lato AI is working…' : 'Ask Lato AI'}</span>
    {open ? <ChevronDown size={15} /> : <ArrowUpRight size={15} />}
  </button>;
}

/** Lets a desktop/tablet AI window move by its header while keeping every
 * edge reachable. On phone widths the existing bottom-sheet layout wins. */
export function useLatoAiPanelDrag(open: boolean) {
  const panelRef = useRef<HTMLElement>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const offsetRef = useRef(offset);
  offsetRef.current = offset;
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ pointerId: number; startX: number; startY: number; x: number; y: number; rect: DOMRect } | null>(null);

  const clamp = useCallback((rect: DOMRect, dx: number, dy: number) => {
    const stage = panelRef.current?.closest('.arch-stage-scene.is-active') as HTMLElement | null;
    const stageRect = stage?.closest('.arch-stage-deck')?.getBoundingClientRect() ?? stage?.getBoundingClientRect();
    const decision = stage?.querySelector('.arch-stage-decision') as HTMLElement | null;
    const decisionTop = decision?.getBoundingClientRect().top;
    const left = Math.max(8, stageRect?.left ?? 8);
    const right = Math.min(window.innerWidth - 8, stageRect?.right ?? window.innerWidth - 8);
    const top = Math.max(8, stageRect?.top ?? 8);
    const bottom = Math.min(window.innerHeight - 8, decisionTop ? decisionTop - 8 : stageRect?.bottom ?? window.innerHeight - 8);
    const x = Math.min(Math.max(rect.left + dx, left), Math.max(left, right - rect.width));
    const y = Math.min(Math.max(rect.top + dy, top), Math.max(top, bottom - rect.height));
    return { x: x - rect.left, y: y - rect.top };
  }, []);

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (window.innerWidth <= 640 || event.button !== 0 || (event.target as HTMLElement).closest('button, input, select, textarea, a')) return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    drag.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, x: offset.x, y: offset.y, rect };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
    event.preventDefault();
  }, [offset]);

  const onPointerMove = useCallback((event: React.PointerEvent<HTMLElement>) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const next = clamp(current.rect, event.clientX - current.startX, event.clientY - current.startY);
    setOffset({ x: current.x + next.x, y: current.y + next.y });
  }, [clamp]);

  const onPointerEnd = useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (drag.current?.pointerId !== event.pointerId) return;
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    setDragging(false);
  }, []);

  // Measure the room above the panel's containing block before paint. This
  // supports both the zero-height architecture dock and document workspaces
  // without collapsing the conversation or covering the stage navigation.
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const panel = panelRef.current;
      if (panel && window.getComputedStyle(panel).position === 'fixed') { setAvailableHeight(null); return; }
      const dock = panel?.closest('.arch-architecture-ai-dock');
      const stage = panel?.closest('.arch-stage-scene.is-active');
      if (!panel || !stage) { setAvailableHeight(null); return; }
      const parent = dock || panel.offsetParent;
      if (!(parent instanceof HTMLElement)) { setAvailableHeight(null); return; }
      const dockBottom = parent.getBoundingClientRect().bottom;
      const stageTop = Math.max(stage.getBoundingClientRect().top, stage.closest('.arch-stage-deck')?.getBoundingClientRect().top ?? 0);
      if (!dockBottom || dockBottom <= stageTop) return;
      const bottomInset = Number.parseFloat(window.getComputedStyle(panel).bottom) || 0;
      const next = Math.max(160, Math.min(560, dockBottom - bottomInset - Math.max(8, stageTop) - 12));
      setAvailableHeight(previous => previous === next ? previous : next);
    };
    measure();
    const stage = panelRef.current?.closest('.arch-stage-scene.is-active');
    const observer = typeof ResizeObserver !== 'undefined' && stage ? new ResizeObserver(measure) : null;
    if (stage && observer) observer.observe(stage);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const keepVisible = () => {
      if (window.innerWidth <= 640) {
        setOffset(previous => previous.x || previous.y ? { x: 0, y: 0 } : previous);
        return;
      }
      const panel = panelRef.current;
      const parent = panel?.offsetParent;
      if (!panel || !(parent instanceof HTMLElement)) return;
      // Opening animates scale/translation. Clamp the final layout, not a
      // transient animation frame which would leave a permanent wrong offset.
      const parentRect = parent.getBoundingClientRect();
      const rect = new DOMRect(
        parentRect.left + parent.clientLeft + panel.offsetLeft + offsetRef.current.x,
        parentRect.top + parent.clientTop + panel.offsetTop + offsetRef.current.y,
        panel.offsetWidth, panel.offsetHeight,
      );
      const next = clamp(rect, 0, 0);
      if (next.x || next.y) setOffset(previous => ({ x: previous.x + next.x, y: previous.y + next.y }));
    };
    const stage = panelRef.current?.closest('.arch-stage-scene.is-active');
    const observer = typeof ResizeObserver !== 'undefined' && stage ? new ResizeObserver(keepVisible) : null;
    if (stage && observer) observer.observe(stage);
    window.addEventListener('resize', keepVisible);
    const frame = window.requestAnimationFrame(keepVisible);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', keepVisible);
      window.cancelAnimationFrame(frame);
    };
  }, [availableHeight, clamp, open]);

  return {
    panelRef,
    panelClass: dragging ? ' is-dragging' : '',
    panelStyle: {
      ...(offset.x || offset.y ? { transform: `translate3d(${offset.x}px, ${offset.y}px, 0)` } : {}),
      ...(availableHeight !== null ? { maxHeight: availableHeight } : {}),
    },
    handleProps: { onPointerDown, onPointerMove, onPointerUp: onPointerEnd, onPointerCancel: onPointerEnd },
  };
}
